<template>
  <div>
    <div ref="slider" class="range-selector minmax" :style="sliderStyle">
      <input
        id="min"
        type="range"
        name="min"
        :min="min"
        :max="max"
        :value="minValue"
        :step="step"
        @input="updateMinValue($event)"
      />
      <input
        id="max"
        type="range"
        name="max"
        :min="min"
        :max="max"
        :value="maxValue"
        :step="step"
        @input="updateMaxValue($event)"
      />
      <div class="progress-bar"></div>
    </div>

    <div class="d-flex align-items-center justify-content-between mt-2">
      <span>{{ min }} {{ unit }}</span>
      <span>{{ max }} {{ unit }}</span>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    min: {
      type: Number,
      default: 0,
    },
    max: {
      type: Number,
      default: 100,
    },
    step: {
      type: Number,
      default: 1,
    },
    minValue: {
      type: [Number, String],
      default: 0,
    },
    maxValue: {
      type: [Number, String],
      default: 50,
    },
    unit: {
      type: String,
      default: "%",
    },
    minmaxIndicator: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      slider: null,
    };
  },

  computed: {
    sliderStyle() {
      return {
        "--progressLeft": `${this.getPercent(
          this.minValue,
          this.min,
          this.max
        )}%`,
        "--progressRight": `${this.getPercent(
          this.maxValue,
          this.min,
          this.max
        )}%`,
      };
    },
  },

  mounted() {
    this.slider = this.$refs.slider;
  },

  methods: {
    getPercent(value, min, max) {
      return ((value - min) / (max - min)) * 100;
    },

    updateMinValue(event) {
      if (event.target.value > this.maxValue) {
        this.$emit("update:minValue", this.maxValue - 1);
        event.target.value = this.maxValue - 1;
      } else {
        this.$emit("update:minValue", parseFloat(event.target.value));
      }
    },

    updateMaxValue(event) {
      if (event.target.value < this.minValue) {
        this.$emit("update:maxValue", this.minValue + 1);
        event.target.value = this.minValue + 1;
      } else {
        this.$emit("update:maxValue", parseFloat(event.target.value));
      }
    },
  },
};
</script>
<style scoped>
.range-selector {
  --trackHeight: 6px;
  --thumbRadius: 24px;
  --progressLeft: 0%;
  --progressRight: 100%;
  --unfilledStripColor: #dfdfdf;
  --filledStripColor: #714bd4;
  --filledCircleColor: #714bd4;
  --circleBorderColor: #f3e8fc;

  position: relative;
  height: var(--trackHeight);
  border-radius: 999px;
  margin: 0.5rem 0;
}

/* style the input element with type "range" */
.range-selector input[type="range"] {
  position: relative;
  appearance: none;
  background: none;
  border-radius: 999px;
  z-index: 2;
  height: 100%;
  pointer-events: none;
}

/* ::before element to replace the slider track */
.range-selector input[type="range"]::before {
  content: "";
  display: block;
  position: absolute;
  width: var(--ProgressPercent, 100%);
  height: 100%;
  border-radius: 999px;
  background: var(--filledStripColor);
}

/* `::-webkit-slider-runnable-track` targets the track (background) of a range slider in chrome and safari browsers. */
.range-selector input[type="range"]::-webkit-slider-runnable-track {
  appearance: none;
  height: var(--trackHeight);
  border-radius: 999px;
  background: var(--filledStripColor);
}

.range-selector input[type="range"]::-webkit-slider-thumb {
  position: relative;
  width: var(--thumbRadius);
  height: var(--thumbRadius);
  margin-top: calc((var(--trackHeight) - var(--thumbRadius)) / 2);
  border-radius: 50%;
  background: var(--filledCircleColor);
  border: 4px solid var(--circleBorderColor);
  pointer-events: all;
  appearance: none;
  z-index: 3;
}

.range-selector .progress-bar {
  position: absolute;
  height: 100%;
  left: var(--progressLeft);
  right: calc(100% - var(--progressRight));
  background: var(--filledStripColor);
  border-radius: 999px;
  z-index: 1;
}

/* `::-moz-range-track` targets the track (background) of a range slider in Mozilla Firefox. */
.range-selector.default input[type="range"]::-moz-range-track {
  appearance: none;
  height: var(--trackHeight);
  border-radius: 999px;
  background: var(--filledStripColor);
}

.range-selector input[type="range"]::-moz-range-thumb {
  position: relative;
  box-sizing: border-box;
  width: var(--thumbRadius);
  height: var(--thumbRadius);
  margin-top: calc((var(--trackHeight) - var(--thumbRadius)) / 2);
  border-radius: 999px;
  pointer-events: all;
  appearance: none;
  z-index: 1;
  background: var(--filledCircleColor);
  border: 4px solid var(--circleBorderColor);
}

.range-selector.minmax {
  position: relative;
  height: var(--trackHeight);
  border-radius: 999px;
  margin: 0.5rem 0;
  --progressLeft: 0%;
  --progressRight: 0%;
  background: var(--unfilledStripColor);
}

.range-selector .minmax-indicator {
  position: absolute;
  height: 100%;
  pointer-events: none;
  left: var(--thumbRadius);
  right: var(--thumbRadius);
}

.range-selector .minmax-indicator::before {
  content: "";
  position: absolute;
  height: 100%;
  left: var(--progressLeft);
  right: var(--progressRight);
  background: var(--filledStripColor);
}

.range-selector.minmax input[type="range"] {
  position: absolute;
  width: 100%;
}

.range-selector.minmax input[type="range"][name="max"] {
  left: 0;
}

.range-selector.minmax input[type="range"]::-webkit-slider-runnable-track {
  background: none;
}

.range-selector.minmax input[type="range"]::before {
  display: none;
}

.minmax-inputs {
  display: flex;
  justify-content: space-between;
}
</style>
